import { useState, useEffect, useRef } from "react";
import {
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useAuth } from "../../../contexts/AuthContext";
import getApiInstance from "../../../services/api";
import { DateToBr } from "../../../hooks/Utils";

// Componente para TableCell com texto em negrito
const TableCellStrong = (props) => {
  return <TableCell {...props} sx={{ fontWeight: "bold", ...props.sx }} />;
};

const GscemiPayment = () => {
  const { system, parameters } = useAuth();
  const [searchLoading, setSearchLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [branches, setBranches] = useState([]);
  const [orders, setOrders] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedInstallments, setSelectedInstallments] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [caixaNotOpenDialogOpen, setCaixaNotOpenDialogOpen] = useState(false);

  const itensEncontradosRef = useRef(null);
  const parcelasRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [paymentDetailsDialogOpen, setPaymentDetailsDialogOpen] =
    useState(false);
  const [paymentMethod, setPaymentMethod] = useState("1"); // 1=débito, 2=crédito, 3=pix
  const [creditInstallments, setCreditInstallments] = useState(1);

  // SearchForm state
  const [searchForm, setSearchForm] = useState({
    // dateRange: {
    //   startDate: subDays(new Date(), 7),
    //   endDate: new Date(),
    // },
    subscriptionId: "",
    dealer: "",
    additionalDealer: "",
    branchId: null,
    onlyOpen: true,
  });

  // Carregar a lista de cemitérios ao iniciar
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        // setLoading(true);
        const api = getApiInstance(system);
        const response = await api.get("/data/branches");
        setBranches(response.data);
      } catch (err) {
        setError("Erro ao carregar lista de cemitérios");
        console.error(err);
      } finally {
        // setLoading(false);
      }
    };

    fetchBranches();
  }, [system]);

  // Função para buscar ordens
  const handleSearch = async () => {
    try {
      setSearchLoading(true);
      setError("");
      setSuccess("");
      setOrders([]);
      setInstallments([]);
      setSelectedOrder(null);

      const api = getApiInstance(system);
      const payload = {
        // dateRange: {
        //   startDate: format(searchForm.dateRange.startDate, 'yyyy-MM-dd'),
        //   endDate: format(searchForm.dateRange.endDate, 'yyyy-MM-dd'),
        // },
        subscriptionId: searchForm.subscriptionId,
        dealer: searchForm.dealer,
        additionalDealer: searchForm.additionalDealer,
        branchId: searchForm.branchId?.code || "",
        onlyOpen: searchForm.onlyOpen,
      };
      const response = await api.post("/orders", payload);
      setOrders(response.data);
      setTimeout(() => {
        window.scrollTo({
          top: itensEncontradosRef?.current?.offsetTop,
          behavior: "smooth",
        });
      }, 300);

      if (response.data.length === 0) {
        setSuccess("Nenhum registro encontrado para os critérios informados.");
      }
    } catch (err) {
      setError(
        "Erro ao buscar registros: " +
        (err.response?.data?.message || err.message)
      );
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para selecionar uma ordem e buscar suas parcelas
  const handleOrderSelect = async (order) => {
    try {
      setSearchLoading(true);
      setError("");
      setSelectedInstallments([]);

      if (
        !order ||
        (selectedOrder && selectedOrder.Inscricao === order.Inscricao)
      ) {
        setSelectedOrder(null);
        return;
      }

      const api = getApiInstance(system);

      if (system === "gscemi") {

        const ok = await api.get(`validate/caixa/${order.idCemiterio}`)

        if (ok.status !== 200 || !ok.data.ok) {
          setDialogOpen(false);
          setCaixaNotOpenDialogOpen(true)
          return
        }
      }

      const response = await api.get(`/orders/installments/${order.Inscricao}`);

      setInstallments(response.data);
      setSelectedOrder(order);
      setTimeout(() => {
        window.scrollTo({
          top: parcelasRef?.current?.offsetTop,
          behavior: "smooth",
        });
      }, 300);
    } catch (err) {
      setError(
        "Erro ao buscar parcelas: " +
        (err.response?.data?.message || err.message)
      );
      setCaixaNotOpenDialogOpen(false)
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para selecionar/desselecionar parcelas
  const toggleInstallmentSelection = (installment) => {
    // Verificar se a parcela já está paga
    if (installment.Pagamento !== null) return;

    setSelectedInstallments((prev) => {
      const isSelected = prev.some((item) => item.Id === installment.Id);

      if (isSelected) {
        return prev.filter((item) => item.Id !== installment.Id);
      } else {
        return [...prev, installment];
      }
    });
  };

  // Função para processar o pagamento
  const handlePayment = async () => {
    try {
      setSearchLoading(true);
      setError("");

      const api = getApiInstance(system);
      const payload = {
        funeraria: selectedOrder.idCemiterio,
        numOrdem: selectedOrder.Inscricao,
        seq: selectedInstallments.map((item) => item.Id),
        forma: paymentMethod,
        qtParcelas: creditInstallments,
      };

      setDialogOpen(true);

      const response = await api.post("/payment/request", payload);

      if (!response.data.ok) {
        setError(response.data.message || "Erro ao processar pagamento");
        return;
      }

      setTransaction(response.data.posTransaction);

      console.log(response.data.posTransaction);

      // Iniciar verificação de status
      checkPaymentStatus(response.data.posTransaction.id);
    } catch (err) {
      setError(
        "Erro ao processar pagamento: " +
        (err.response?.data?.message || err.message)
      );
      console.error(err);
      setDialogOpen(false);
      setCaixaNotOpenDialogOpen(false)
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para verificar status do pagamento
  const checkPaymentStatus = async (transactionId) => {
    try {
      setStatusLoading(true);
      const api = getApiInstance(system);

      // Loop de verificação a cada 3 segundos
      const intervalId = setInterval(async () => {
        try {
          const response = await api.get(`/payment/status/${transactionId}`);
          console.log(response.data);
          if (response.data.status.Pagamentos) {
            clearInterval(intervalId);
            setPaymentResult(response.data);
            //
            handleOrderSelect(selectedOrder);
            // console.log(response.data);
            setStatusLoading(false);
          } else if (response.data.status === "cancelada") {
            clearInterval(intervalId);
            setPaymentResult(null);
            setStatusLoading(false);
          }
        } catch (err) {
          console.error("Erro ao verificar status:", err);
        }
      }, 3000);

      // Limpar o intervalo quando o componente for desmontado
      return () => clearInterval(intervalId);
    } catch (err) {
      setStatusLoading(false);
      setError(
        "Erro ao verificar status do pagamento: " +
        (err.response?.data?.message || err.message)
      );
      console.error(err);
    }
  };

  // Função para cancelar o pagamento
  const handleCancelPayment = async () => {
    try {
      if (!transaction) return;

      const api = getApiInstance(system);
      await api.delete(`/payment/cancelRequest/${transaction.id}`);

      setDialogOpen(false);
      setTransaction(null);
      setPaymentResult(null);
    } catch (err) {
      setError(
        "Erro ao cancelar pagamento: " +
        (err.response?.data?.message || err.message)
      );
      console.error(err);
    }
  };

  // Função para fechar o diálogo
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTransaction(null);
    setPaymentResult(null);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Pagamento GSCEMI
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros de Busca
        </Typography>

        <Grid container spacing={2}>
          {/* <Grid size={{ xs: 12 }}>
            <DateRangeSelector
              startDate={searchForm.dateRange.startDate}
              endDate={searchForm.dateRange.endDate}
              onChange={(dateRange) =>
                setSearchForm((prev) => ({ ...prev, dateRange }))
              }
            />
          </Grid> */}

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Inscrição"
              value={searchForm.subscriptionId}
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  subscriptionId: e.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Concessionário"
              value={searchForm.dealer}
              onChange={(e) =>
                setSearchForm((prev) => ({ ...prev, dealer: e.target.value }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Administrador Provisório"
              value={searchForm.additionalDealer}
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  additionalDealer: e.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={branches}
              getOptionLabel={(option) => option.name}
              value={searchForm.branchId}
              onChange={(_, newValue) => {
                setSearchForm((prev) => ({ ...prev, branchId: newValue }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Cemitério" fullWidth />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchForm.onlyOpen}
                  onChange={(e) =>
                    setSearchForm((prev) => ({
                      ...prev,
                      onlyOpen: e.target.checked,
                    }))
                  }
                />
              }
              label="Apenas Pendentes"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? <CircularProgress size={24} /> : "Listar"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {orders.length > 20 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {"Exibindo os primeiros 21 resultados - refine sua busca"}
        </Alert>
      )}

      {orders.length > 0 && (
        <Box sx={{ p: isMobile ? 1 : 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom ref={itensEncontradosRef}>
            Itens Encontrados
          </Typography>
          {isMobile ? (
            <Grid container spacing={2}>
              {orders.map((order) =>
                !selectedOrder ||
                  selectedOrder.Inscricao === order.Inscricao ? (
                  <Grid item size={{ xs: 12, md: 12 }} key={order.Inscricao}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          Inscrição: {order.Inscricao}
                        </Typography>
                        <Typography variant="body2">
                          Cemitério: {order.Cemiterio}
                        </Typography>
                        <Typography variant="body2">
                          Concessionário: {order.Concessionario}
                        </Typography>
                        <Typography variant="body2">
                          CPF Concessionário: {order.CPF_Concessionario}
                        </Typography>
                        {order.Administrador_Provisorio && (
                          <Typography variant="body2">
                            Adm. Provisório: {order.Administrador_Provisorio}
                          </Typography>
                        )}
                        {order.CPF_ADM_Provisorio && (
                          <Typography variant="body2">
                            CPF Adm. Provisório: {order.CPF_ADM_Provisorio}
                          </Typography>
                        )}
                        {/* Adicione outros campos se necessário */}
                      </CardContent>
                      <CardActions>
                        {!selectedOrder ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOrderSelect(order)}
                          >
                            Selecionar
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOrderSelect(null)}
                          >
                            X
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                ) : null
              )}
            </Grid>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCellStrong>Cemitério</TableCellStrong>
                    <TableCellStrong>Inscrição</TableCellStrong>
                    <TableCellStrong>Concessionário</TableCellStrong>
                    <TableCellStrong>CPF Concessionário</TableCellStrong>
                    <TableCellStrong>Administrador Provisório</TableCellStrong>
                    <TableCellStrong>CPF ADM Provisório</TableCellStrong>
                    <TableCellStrong>Ações</TableCellStrong>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders
                    .filter(
                      (order) =>
                        !selectedOrder ||
                        selectedOrder.Inscricao === order.Inscricao
                    )
                    .map((order) => (
                      <TableRow
                        key={order.Inscricao}
                        sx={{
                          backgroundColor:
                            selectedOrder &&
                              selectedOrder.Inscricao === order.Inscricao
                              ? "#f0f0f0"
                              : "#fff",
                        }}
                      >
                        <TableCell>{order.Cemiterio}</TableCell>
                        <TableCell>{order.Inscricao}</TableCell>
                        <TableCell>{order.Concessionario}</TableCell>
                        <TableCell>{order.CPF_Concessionario}</TableCell>
                        <TableCell>{order.Administrador_Provisorio}</TableCell>
                        <TableCell>{order.CPF_ADM_Provisorio}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOrderSelect(order)}
                            sx={{
                              backgroundColor:
                                selectedOrder &&
                                  selectedOrder.Inscricao === order.Inscricao
                                  ? "#ff0000"
                                  : "#1976d2",
                            }}
                          >
                            {selectedOrder &&
                              selectedOrder.Inscricao === order.Inscricao
                              ? "X"
                              : "Selecionar"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
      {selectedOrder && installments.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom ref={parcelasRef}>
            Parcelas da Inscrição {selectedOrder.Inscricao}
          </Typography>

          {isMobile ? (
            <Box>
              {installments
                .filter(
                  (installment) =>
                    !searchForm.onlyOpen || !installment.Pagamento
                )
                .map((installment) => (
                  <Card
                    key={installment.Id}
                    sx={{
                      mb: 2,
                      backgroundColor: installment.Pagamento
                        ? "#f0f0f0"
                        : "#fff",
                      border: selectedInstallments.some(
                        (item) => item.Id === installment.Id
                      )
                        ? "2px solid #1976d2"
                        : "1px solid #ccc",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleInstallmentSelection(installment);
                    }}
                  >
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedInstallments.some(
                              (item) => item.Id === installment.Id
                            )}
                            onChange={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              console.log("Checkbox clicked");
                              toggleInstallmentSelection(installment);
                            }}
                            disabled={installment.Pagamento !== null}
                            slotProps={{
                              input: { "aria-label": "Selecionar parcela" },
                            }}
                            sx={{
                              "& .MuiSvgIcon-root": {
                                pointerEvents: "auto",
                              },
                              padding: "4px",
                            }}
                            disableRipple={false}
                          />
                        }
                        label="Selecionar"
                        sx={{ width: "100%" }}
                      />
                      {/* <Typography variant="subtitle1">
                        Parcela: {installment.Id}
                      </Typography> */}
                      <Typography variant="subtitle1">
                        Referência: {installment.referencia}
                      </Typography>
                      <Typography variant="body2">
                        Valor: R$ {installment.Valor}
                      </Typography>
                      <Typography variant="body2">
                        Vencimento: {DateToBr(installment.Vencimento)}
                      </Typography>
                      <Typography variant="body2">
                        Status:
                        {installment.Pagamento
                          ? `Paga em ${DateToBr(installment.Pagamento)}`
                          : "Em aberto"}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCellStrong>ID</TableCellStrong>
                    <TableCellStrong>Inscrição</TableCellStrong>
                    <TableCellStrong>Tipo Mensalidade</TableCellStrong>
                    <TableCellStrong>Referência</TableCellStrong>
                    <TableCellStrong>Vencimento</TableCellStrong>
                    <TableCellStrong align="right">Valor</TableCellStrong>
                    <TableCellStrong>Pagamento</TableCellStrong>
                    <TableCellStrong align="right">Valor Pago</TableCellStrong>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {installments
                    .filter(
                      (installment) =>
                        !searchForm.onlyOpen || !installment.Pagamento
                    )
                    .map((installment) => (
                      <TableRow
                        key={installment.Id}
                        selected={selectedInstallments.some(
                          (item) => item.Id === installment.Id
                        )}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedInstallments.some(
                              (item) => item.Id === installment.Id
                            )}
                            onChange={() =>
                              toggleInstallmentSelection(installment)
                            }
                            disabled={installment.Pagamento !== null}
                          />
                        </TableCell>
                        <TableCell>{installment.Id}</TableCell>
                        <TableCell>{installment.Inscricao}</TableCell>
                        <TableCell>{installment.TipoMensalidade}</TableCell>
                        <TableCell>{installment.referencia}</TableCell>
                        <TableCell>
                          {DateToBr(installment.Vencimento)}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(installment.Valor).toLocaleString(
                            "pt-BR",
                            {
                              style: "currency",
                              currency: "BRL",
                            }
                          )}
                        </TableCell>
                        <TableCell>
                          {installment.Pagamento
                            ? DateToBr(installment.Pagamento)
                            : "-"}
                        </TableCell>
                        <TableCell align="right">
                          {installment.ValorPago
                            ? parseFloat(installment.ValorPago).toLocaleString(
                              "pt-BR",
                              {
                                style: "currency",
                                currency: "BRL",
                              }
                            )
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setPaymentDetailsDialogOpen(true)}
              disabled={selectedInstallments.length === 0}
            >
              Efetuar Pagamento
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={paymentDetailsDialogOpen}
        onClose={() => setPaymentDetailsDialogOpen(false)}
      >
        <DialogTitle>Detalhes do Pagamento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ md: 6 }}>
              <FormControl>
                <FormLabel>Inscrição</FormLabel>
                <Typography variant="h5" gutterBottom>
                  {selectedOrder ? selectedOrder.Inscricao : "Nenhuma seleção"}
                </Typography>
              </FormControl>
            </Grid>
            <Grid size={{ md: 6 }}>
              <FormControl size={{ md: 6 }}>
                <FormLabel>Referências</FormLabel>
                <Typography variant="h7" gutterBottom>
                  {selectedInstallments.map((i) => i.referencia).join(" / ")}
                </Typography>
              </FormControl>
            </Grid>
          </Grid>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Forma de Pagamento</FormLabel>
            <RadioGroup
              row
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <FormControlLabel value="1" control={<Radio />} label="Débito" />
              <FormControlLabel value="2" control={<Radio />} label="Crédito" />
              <FormControlLabel value="4" control={<Radio />} label="Pix" />
            </RadioGroup>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel>Valor Total</FormLabel>
            <Typography variant="h4" color="primary" gutterBottom>
              {selectedInstallments
                .reduce((total, item) => total + item.Valor, 0)
                .toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
            </Typography>
          </FormControl>
          {paymentMethod === "2" && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <FormLabel>Parcelas</FormLabel>
              <Select
                value={creditInstallments}
                onChange={(e) => setCreditInstallments(Number(e.target.value))}
              >
                {Array.from(
                  { length: parameters?.maxInstallments || 6 },
                  (_, i) => i + 1
                ).map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}x {num > 1 ? "parcelas" : "parcela"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDetailsDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setPaymentDetailsDialogOpen(false);
              handlePayment();
            }}
            variant="contained"
            color="primary"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={caixaNotOpenDialogOpen} fullWidth maxWidth="md">
        <DialogTitle>Abertura de movimentação não permitida pois não há caixa aberto para o cemitério informado.</DialogTitle>
        <DialogActions>
          <Button onClick={() => setCaixaNotOpenDialogOpen(false)} color="error">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de acompanhamento do pagamento */}
      <Dialog open={dialogOpen} fullWidth maxWidth="md">
        <DialogTitle>Processando Pagamento</DialogTitle>
        <DialogContent>
          {statusLoading && !paymentResult && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 3,
              }}
            >
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>
                Aguardando confirmação do pagamento...
              </Typography>
            </Box>
          )}

          {paymentResult && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Pagamento Concluído
              </Typography>

              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  overflow: "auto",
                }}
              >
                {JSON.stringify(paymentResult.pagamento, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!paymentResult && (
            <Button onClick={handleCancelPayment} color="error">
              Cancelar
            </Button>
          )}
          {paymentResult && (
            <Button onClick={handleCloseDialog} color="primary">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GscemiPayment;
